"use strict";
/**
 * Counterweighted allocation arithmetic (BEN-141, 18 August call). ONE copy, for
 * both clients.
 *
 * Anthony: "if I make it 100, that's taking all the money. Everything else
 * should go zero." Alex: "if one's at 40%, the other one's at 20 each."
 *
 * Before this, the five sliders moved independently and the set could reach
 * 500%. The server did not stop it either: `AllocationUpdateSerializer`
 * validated each `pct` to [0,100] but never the total (it does now, #854).
 *
 * THE INVARIANT IS "NEVER EXCEEDS 100", NOT "ALWAYS EQUALS 100". Deliberate
 * under-allocation is a legitimate choice, and Anthony's words were about the over
 * case only. So dragging UP counterweights the others down; dragging DOWN leaves
 * the remainder unallocated for the page to surface.
 *
 * Proportional REDUCTION is always well defined: removing `D` from others summing
 * `X > 0` takes `D × pct/X` from each, and when `X = 0` the total is just the
 * edited bucket, capped at 100, so no reduction is ever needed. The undefined 0/0
 * split only arises when distributing UPWARD, which this rule does not do.
 *
 * FP-085 (Paul, 2026-10-05): STORED PERCENTAGES STAY EXACT. Round only for display
 * and for the bucket being dragged. This module used to round EVERY bucket to a
 * whole number on every edit, and both clients saved the whole set, so dragging
 * bucket A re-saved bucket B at 24% instead of its stored 23.53% and B's dollars
 * moved without anyone touching it (rule 11). Already-rounded rows cannot be
 * restored: their fractions are gone.
 *
 * So the arithmetic works in HUNDREDTHS of a percent, the server's precision
 * (`DecimalField(decimal_places=2)`): totals are exact, nothing is sent with a
 * third decimal, and a set that sums to 100 never reads as 100.00000000000001.
 *
 * Moved here from `frontend/src/lib/allocationMechanics.ts` and
 * `mobile/src/utils/allocationMechanics.ts` (identical copies) in the FP-085 fix,
 * the "fix it there once" Paul asked for. Each client's tests import this file.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.allocationState = exports.allocationTotal = exports.ALLOCATION_CEILING = void 0;
exports.counterweightAllocation = counterweightAllocation;
exports.counterweightLocked = counterweightLocked;
exports.lockedShare = lockedShare;
exports.counterweightedOthers = counterweightedOthers;
exports.pctFromAmount = pctFromAmount;
exports.amountFromPct = amountFromPct;
exports.parseAmountInput = parseAmountInput;
/** Hundredths per percent: the server stores percentages to two decimal places. */
const HUNDREDTHS = 100;
const MAX_UNITS = 100 * HUNDREDTHS;
exports.ALLOCATION_CEILING = 100;
/** A percentage in hundredths, clamped to [0, 100]. Keeps the stored precision. */
function toUnits(pct) {
    return Math.max(0, Math.min(MAX_UNITS, Math.round(pct * HUNDREDTHS)));
}
function fromUnits(units) {
    return units / HUNDREDTHS;
}
/** The dragged bucket: a whole percentage, as a slider gives it. */
function wholePct(n) {
    return Math.max(0, Math.min(100, Math.round(n)));
}
/**
 * Apportion `target` across `weights` as integers summing to EXACTLY `target`,
 * using the largest-remainder (Hare) method. Ties break toward the lower index so
 * the result is deterministic (and therefore testable).
 */
function apportion(weights, target) {
    const total = weights.reduce((s, w) => s + w, 0);
    if (total <= 0 || target <= 0)
        return weights.map(() => 0);
    const exact = weights.map((w) => (w * target) / total);
    const floors = exact.map(Math.floor);
    let remaining = target - floors.reduce((s, f) => s + f, 0);
    const order = exact
        .map((value, index) => ({ index, frac: value - Math.floor(value) }))
        .sort((a, b) => b.frac - a.frac || a.index - b.index);
    const out = [...floors];
    for (const { index } of order) {
        if (remaining <= 0)
            break;
        out[index] += 1;
        remaining -= 1;
    }
    return out;
}
/**
 * Apply a single-bucket edit under the counterweighting rule.
 *
 * @param pcts    current percentages, in card order, as stored (two decimals)
 * @param index   the bucket the user is dragging
 * @param next    the value they dragged it to (rounded to a whole percentage)
 * @param ceiling what the edited buckets may share (100 less any fixed-dollar
 *                buckets' share); kept to the hundredth, never floored to a whole
 * @returns a new array; `allocationTotal(result) <= ceiling` always
 */
function counterweightAllocation(pcts, index, next, ceiling = exports.ALLOCATION_CEILING) {
    // Floored, never rounded: rounding a fractional ceiling up would let the set
    // exceed what the fixed-dollar buckets leave by a hundredth.
    const ceilingUnits = Number.isFinite(ceiling)
        ? Math.max(0, Math.min(MAX_UNITS, Math.floor(ceiling * HUNDREDTHS + 1e-6)))
        : MAX_UNITS;
    const editedUnits = Math.min(wholePct(next) * HUNDREDTHS, ceilingUnits);
    // Every bucket but the dragged one keeps its stored value exactly (FP-085).
    const units = pcts.map((p, i) => (i === index ? editedUnits : toUnits(p)));
    const othersUnits = units.reduce((s, u, i) => (i === index ? s : s + u), 0);
    if (editedUnits + othersUnits <= ceilingUnits) {
        // Under or exactly at the ceiling: the others are untouched and the
        // remainder stays unallocated, the legitimate "left to allocate" state.
        return units.map(fromUnits);
    }
    // Over the ceiling: pull the others down proportionally so the set lands on
    // exactly the ceiling. Reaching here implies othersUnits > 0, because the edited
    // bucket alone is capped at the ceiling, so `apportion` never divides by zero.
    //
    // Whole percentages stay whole: when the target and every other bucket are whole,
    // apportion in whole percents (the result an all-integer set always had). A set
    // holding a fractional share is apportioned in hundredths instead, so it still
    // lands on the ceiling exactly without rounding anyone to a whole number.
    const targetUnits = ceilingUnits - editedUnits;
    const otherIdx = units.map((_, i) => i).filter((i) => i !== index);
    const allWhole = targetUnits % HUNDREDTHS === 0 &&
        otherIdx.every((i) => units[i] % HUNDREDTHS === 0);
    const grain = allWhole ? HUNDREDTHS : 1;
    const shares = apportion(otherIdx.map((i) => units[i] / grain), targetUnits / grain);
    otherIdx.forEach((i, k) => {
        units[i] = shares[k] * grain;
    });
    return units.map(fromUnits);
}
/**
 * Total of a set, summed in hundredths so a set that holds fractional shares and
 * sums to 100 reads as exactly 100 (in floats, 13 + 12.43 + 74.57 is
 * 100.00000000000001, which would read as "over").
 */
const allocationTotal = (pcts) => fromUnits(pcts.reduce((s, p) => s + toUnits(p), 0));
exports.allocationTotal = allocationTotal;
/**
 * The counterweight with LOCKED buckets (FP-037; web and mobile agreed in writing,
 * 2026-10-05). A locked bucket is never moved by another bucket's edit, but its
 * share still counts toward the ceiling, so the stored set never passes 100 (the
 * FP-085 invariant). Locked: a fixed-dollar bucket, and an unfunded one (it holds a
 * share but funds nothing, e.g. the Debt Payoff Fund with no active debt).
 * `locked[index]` is ignored: the bucket being edited is always editable.
 *
 * Both clients' Forecast sections call this; it used to be a local
 * `counterweightUnlocked` in each, with fixed-dollar as the only lock.
 */
function counterweightLocked(pcts, index, next, locked) {
    const editable = pcts
        .map((_, i) => (!locked[i] || i === index ? i : -1))
        .filter((i) => i >= 0);
    const room = Math.max(0, exports.ALLOCATION_CEILING - lockedShare(pcts, locked, index));
    const adjusted = counterweightAllocation(editable.map((i) => pcts[i]), editable.indexOf(index), Math.min(next, room), room);
    return pcts.map((p, i) => {
        const at = editable.indexOf(i);
        return at >= 0 ? adjusted[at] : p;
    });
}
/** The share held by locked buckets other than `except`, summed in hundredths. The
 *  typed-dollar ceiling: a typed amount can use only what the locked buckets leave. */
function lockedShare(pcts, locked, except) {
    return (0, exports.allocationTotal)(pcts.filter((_, i) => i !== except && locked[i]));
}
/**
 * Did an edit move buckets the user did not touch? (BEN-141 item 7.)
 *
 * Totals do NOT have to equal 100 (under-allocation is a legitimate choice), and
 * `counterweightAllocation` makes over-allocation unreachable through the UI. What
 * was missing is this: pushing one bucket past the ceiling rewrites the others, and
 * nothing said so. True only for the counterweighted case.
 */
function counterweightedOthers(before, after, index) {
    return after.some((value, i) => i !== index && toUnits(value) !== toUnits(before[i]));
}
/**
 * The percentage a typed dollar amount corresponds to (BEN-141 item 2), as a whole
 * percentage: it is the value the dragged (typed) bucket counterweights with. The
 * caller stores the dollar amount itself.
 */
function pctFromAmount(amount, surplus) {
    if (!Number.isFinite(amount) || !Number.isFinite(surplus) || surplus <= 0)
        return 0;
    return wholePct((amount / surplus) * 100);
}
/**
 * The dollar figure a percentage corresponds to, against today's surplus, at the
 * percentage's own precision (FP-085: 23.53% of $2,526.17 is $594.41, not the
 * $606.28 that 24% gives). A settled bucket should show the server's
 * `effective_amount`; this is for a figure the server has not computed yet, such
 * as a slider mid-drag.
 */
function amountFromPct(pct, surplus) {
    if (!Number.isFinite(pct) || !Number.isFinite(surplus))
        return 0;
    return (fromUnits(toUnits(pct)) / 100) * surplus;
}
/**
 * Parse what a user typed into a dollar field: "$1,218.00", "1218", "1,218" ->
 * 1218. Returns null for anything that is not a number, so the caller can leave
 * the field alone rather than coercing a typo to 0.
 */
function parseAmountInput(raw) {
    const cleaned = raw.replace(/[$,\s]/g, "");
    if (cleaned === "" || !/^\d*\.?\d*$/.test(cleaned))
        return null;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
}
/** Compares in hundredths, so a fractional set summing to 100 is "exact". */
const allocationState = (total) => {
    const units = Math.round(total * HUNDREDTHS);
    return units === MAX_UNITS ? "exact" : units < MAX_UNITS ? "under" : "over";
};
exports.allocationState = allocationState;
//# sourceMappingURL=allocationMechanics.js.map